// Packaging.Fidelity — the fidelity contract type + closed loss union
// + the monoid `composeFidelity` primitive.
//
// Per `.docs/v1/design/surface.md` §6.3 and Slice 5 Q1, Q2, Q6.
//
// A `FidelityContract` is a wrapper record around `losses:
// ReadonlySet<CategoricalLoss>` — not a bare set. Cost: two extra
// characters at every call site. Benefit: post-MVP `notes` /
// `applicabilityHint` fields land without reshaping every caller.
//
// `CategoricalLoss` is a **closed union**, extensible only by editing
// core. Most Packaging registry entries have empty `losses` sets, but
// the vocabulary ships so Slice 7 Quality reifications have a stable
// target. `lost_objective_alignment` is the first non-empty use: the
// pqbl-torus → Canvas Common Cartridge packaging drops the objectives
// / skills graph because Common Cartridge has no home for it.
//
// `composeFidelity` is monoid union — associative, commutative, with
// the empty-losses set as identity. Post-MVP path-finding relies on
// this to walk a chain of Packagings and accumulate the total loss.
// Ships now (cheap) so path-finding is a straight-line addition.
export { composeFidelity, emptyFidelity, mkFidelityContract };
const mkFidelityContract = (losses) => ({ losses });
// Empty (identity) contract. Every MVP registry entry uses this;
// callers can reach for it rather than building an empty `Set`.
const emptyFidelity = { losses: new Set() };
// === composeFidelity ===
//
// Monoid union. Given `a: A → B` and `b: B → C`, the composed chain
// `A → C` drops at least everything either link drops. Same union
// under swap (commutative), same union under regrouping (associative),
// unchanged when composed with the empty contract (identity).
const composeFidelity = (a, b) => {
    const merged = new Set(a.losses);
    for (const loss of b.losses)
        merged.add(loss);
    const resolution = [...(a.resolution ?? []), ...(b.resolution ?? [])];
    return resolution.length === 0
        ? { losses: merged }
        : { losses: merged, resolution };
};
//# sourceMappingURL=fidelity.js.map